import { useMemo } from 'react';
import { Text } from '@ubnt/ui-components/aria/Typography/Text';
import { PaletteColor } from '@ubnt/ui-components/aria/Typography/Text/types';
import { useEditorStore } from 'modules/editor/store';
import { useSelector } from 'app/store';
import { devicesDeviceMapWithClientPointerPositionSelector } from 'modules/devices/slice';
import { linksLinkPointerMcsSelector, linksLinkPointerSignalSelector } from 'modules/links/slice';
import { MCS_COLOR } from 'modules/editor/components/CoverageLegend/constants';
import { getPositionByMcs } from 'modules/editor/components/CoverageLegend/utils';

import classes from 'modules/editor/components/CoverageLegend/CoverageLegendThumb.module.scss';
import config from 'styles/config.module.scss';

const LEGEND_WIDTH = Number(config.legendBarWidth);

const roundSignal = (signal?: number) => (signal == null ? signal : Math.ceil(signal));

export function CoverageLegendThumb() {
  const pointerPosition = useEditorStore((state) => state.pointerPosition);
  const isLinkOrDeviceHovered = useEditorStore((state) => !!state.hoverLinkId || !!state.hoverDeviceId);
  const devicesMap = useSelector((state) =>
    pointerPosition && !isLinkOrDeviceHovered ?
      devicesDeviceMapWithClientPointerPositionSelector(state, pointerPosition)
    : undefined
  );
  const pointerMcs = useSelector((state) =>
    devicesMap ? linksLinkPointerMcsSelector(state, devicesMap) : undefined
  ) as keyof typeof MCS_COLOR;
  const pointerSignal = useSelector((state) =>
    devicesMap && pointerMcs != null ? roundSignal(linksLinkPointerSignalSelector(state, devicesMap)) : undefined
  );
  const mcsColor = MCS_COLOR[pointerMcs];

  const textColor = useMemo(() => {
    let color: PaletteColor = 'green-06';
    if (pointerMcs === undefined || pointerMcs < 3) {
      color = 'red-06';
    } else if (pointerMcs < 7) {
      color = 'orange-06';
    }
    return color;
  }, [pointerMcs]);

  const translateX = useMemo(() => {
    if (pointerMcs === 13) {
      return LEGEND_WIDTH;
    }

    return LEGEND_WIDTH * getPositionByMcs(pointerMcs);
  }, [pointerMcs]);

  if (!mcsColor || typeof pointerSignal !== 'number') {
    return null;
  }

  return (
    <div
      className={classes.thumb}
      style={{
        background: mcsColor,
        transform: `translateX(${translateX}px)`,
      }}
    >
      <Text color={textColor} className={classes.thumbText}>
        {pointerSignal} dBm
      </Text>
    </div>
  );
}
